import moment from 'moment';

import logger from 'utils/logger.util';

import fromAnyToBool from '../fromAnyToBool.util';
import fromAnyToInteger from '../fromAnyToInteger';

const L = logger('intl.util');

const ONE_DAY_IN_MILLIS = 24 * 60 * 60 * 1000;

/**
 * Formats a date according to the given options.
 * @param {Date} date - The date to format.
 * @param {Object} options - The options to use for the date formatting.
 * @returns {string} The formatted date.
 */
export const formatDate = (date, options) => {
  const momentDate = moment.isMoment(date) ? date.toDate() : null;
  const dateObject = date instanceof Date ? date : null;
  const stringDate = typeof date === 'string' || typeof date === 'number' ? new Date(date) : null;

  const normalizedDate = momentDate || dateObject || stringDate;

  if (!normalizedDate || Number.isNaN(normalizedDate.getTime())) {
    L.warn('formatDate()', 'Invalid date value:', date);
    return null;
  }

  const isTimeHidden = fromAnyToBool(options?.hideTime);
  const isWeekdayHidden = fromAnyToBool(options?.hideWeekday);
  const isDateHidden = fromAnyToBool(options?.hideDate);
  const isYearHidden = fromAnyToBool(options?.hideYear);

  if (isTimeHidden) {
    const timeDate = new Intl.DateTimeFormat(options?.locale, {
      timeZone: options?.timeZone || 'Europe/London',
      month: options?.month || 'short',
      day: options?.day || 'numeric',
      ...(!isYearHidden ? { year: options?.year || 'numeric' } : {}),
      ...(!isWeekdayHidden ? { weekday: options?.weekday || 'short' } : {}),
      hour12: options?.hour12 ?? true,
    }).format(normalizedDate);
    return timeDate;
  }

  if (isDateHidden) {
    return new Intl.DateTimeFormat(options?.locale, {
      timeZone: options?.timeZone || 'Europe/London',
      hour: options?.hour || 'numeric',
      minute: options?.minute || 'numeric',
      hour12: options?.hour12 ?? true,
    }).format(normalizedDate);
  }

  return new Intl.DateTimeFormat(options?.locale, {
    timeZone: options?.timeZone || 'Europe/London',
    ...(!isYearHidden ? { year: options?.year || 'numeric' } : {}),
    month: options?.month || 'short',
    day: options?.day || 'numeric',
    hour: options?.hour || 'numeric',
    minute: options?.minute || 'numeric',
    ...(!isWeekdayHidden ? { weekday: options?.weekday || 'short' } : {}),
    hour12: options?.hour12 ?? true,
  }).format(normalizedDate);
};

/**
 * Checks if a given date is today or yesterday.
 * @param {Date} date - The date to check.
 * @param {Object} options - The options to use for the date formatting.
 * @returns {Object} An object with the isToday and isYesterday properties.
 */
export const checkRecentDate = (date, options) => {
  if (!(date instanceof Date) || isNaN(date)) {
    L.warn('checkRecentDate()', 'Invalid locale:', options?.locale, ', timeZone:', options?.timeZone, 'or date:', date);
    return {
      isToday: false,
      isYesterday: false,
    };
  }

  const formatter = new Intl.DateTimeFormat(options?.locale, {
    timeZone: options?.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const now = new Date();
  const yesterday = new Date(Date.now() - ONE_DAY_IN_MILLIS);

  const dateParts = formatter.formatToParts(date);
  const nowParts = formatter.formatToParts(now);
  const yesterdayParts = formatter.formatToParts(yesterday);

  const dateYear = fromAnyToInteger(dateParts.find((p) => p.type === 'year')?.value);
  const dateMonth = fromAnyToInteger(dateParts.find((p) => p.type === 'month')?.value);
  const dateDay = fromAnyToInteger(dateParts.find((p) => p.type === 'day')?.value);

  const nowYear = fromAnyToInteger(nowParts.find((p) => p.type === 'year')?.value);
  const nowMonth = fromAnyToInteger(nowParts.find((p) => p.type === 'month')?.value);
  const nowDay = fromAnyToInteger(nowParts.find((p) => p.type === 'day')?.value);

  const yesterdayYear = fromAnyToInteger(yesterdayParts.find((p) => p.type === 'year')?.value);
  const yesterdayMonth = fromAnyToInteger(yesterdayParts.find((p) => p.type === 'month')?.value);
  const yesterdayDay = fromAnyToInteger(yesterdayParts.find((p) => p.type === 'day')?.value);

  const isToday = dateYear === nowYear && dateMonth === nowMonth && dateDay === nowDay;
  const isYesterday = dateYear === yesterdayYear && dateMonth === yesterdayMonth && dateDay === yesterdayDay;

  return {
    isToday,
    isYesterday,
  };
};

/**
 * Determines the UTC start and end `Date`s for a given date in a given timezone.
 * @param {Date} date - The date to determine the start and end for.
 * @param {string} locale - The locale to use for the date formatting.
 * @param {string} timeZone - The timezone to use for the date formatting.
 * @returns {Object} An object with the UTC start and end.
 */
export const determineDayRangeUTCForTimezone = (date, locale, timeZone) => {
  if (!timeZone || !locale) {
    L.warn('determineDayRangeUTCForTimezone()', 'Invalid locale:', locale, 'or timeZone:', timeZone);
    return null;
  }

  const tz = timeZone.trim();

  // 1. Get the calendar date in the target timezone
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const year = fromAnyToInteger(parts.find((p) => p.type === 'year')?.value);
  const month = fromAnyToInteger(parts.find((p) => p.type === 'month')?.value);
  const day = fromAnyToInteger(parts.find((p) => p.type === 'day')?.value);

  // 2. Create UTC midnight for that calendar day
  const utcMidnight = new Date(Date.UTC(year, month - 1, day));

  // 3. Get timezone offset at that moment
  const offsetParts = new Intl.DateTimeFormat(locale, {
    timeZone: tz,
    timeZoneName: 'shortOffset',
  }).formatToParts(utcMidnight);

  const offset = offsetParts.find((p) => p.type === 'timeZoneName')?.value;
  const match = offset.match(/GMT([+-]\d{2})?(?::(\d{2}))?/);

  const offsetHours = fromAnyToInteger(match?.[1] ?? 0);
  const offsetMinutes = fromAnyToInteger(match?.[2] ?? 0);

  const offsetMs = (offsetHours * 60 + Math.sign(offsetHours) * offsetMinutes) * 60 * 1000;

  // 4. Apply offset → local midnight expressed in UTC
  const startUTC = new Date(utcMidnight.getTime() - offsetMs);
  const endUTC = new Date(startUTC.getTime() + ONE_DAY_IN_MILLIS - 1);

  return {
    startUTCDate: startUTC,
    startUTCString: startUTC.toISOString(),
    endUTCDate: endUTC,
    endUTCString: endUTC.toISOString(),
  };
};
/**
 * Determines the UTC midnight and noon for a given date.
 * @param {Date|moment} date - The date to determine the midnight and noon for.
 * @returns {Object} An object with the UTC midnight and noon.
 */
export const determineMidnightUTC = (date) => {
  if (!date) {
    L.warn('determineMidnightUTC()', 'Invalid date:', date);
    return null;
  }

  const normalizedDate = moment.isMoment(date) ? date.toDate() : date instanceof Date ? date : typeof date === 'string' ? new Date(date) : null;

  if (!normalizedDate || Number.isNaN(normalizedDate.getTime())) {
    L.warn('determineMidnightUTC()', 'Invalid date:', date, 'type:', typeof date);
    return null;
  }

  const year = normalizedDate.getFullYear();
  const month = normalizedDate.getMonth();
  const day = normalizedDate.getDate();

  const utcMidnight = new Date(Date.UTC(year, month, day));
  const utcNoon = new Date(Date.UTC(year, month, day, 12, 0, 0));

  return { utcMidnight, utcNoon };
};

/**
 * Converts a date string in `YYYYMMDD` format into a `Date` object (UTC noon).
 *
 * This function validates the input to ensure it is exactly 8 digits long.
 * If the format is invalid, it logs a warning and returns `null`.
 *
 * @param {string} yyyymmdd - The date string in `YYYYMMDD` format.
 * @returns {Date|null} A JavaScript `Date` object representing the given date
 *                      at UTC noon, or `null` if the input is invalid.
 */
export const fromParamToDate = (yyyymmdd) => {
  if (!/^\d{8}$/.test(yyyymmdd)) {
    L.warn('fromParamToDate()', 'Invalid date format. Expected YYYYMMDD');
    return null;
  }

  const year = fromAnyToInteger(yyyymmdd.slice(0, 4));
  const month = fromAnyToInteger(yyyymmdd.slice(4, 6)) - 1; // 0-based
  const day = fromAnyToInteger(yyyymmdd.slice(6, 8));

  // Create a stable reference moment (UTC noon)
  return new Date(Date.UTC(year, month, day, 12, 0, 0));
};

export const isWeekParam = (dateString) => dateString.match(/^(\d{1,2})W(\d{4})$/);

/**
 * Checks if the provided date string matches the week parameter format `NWYYYY`,
 * where N is the week number (1 or 2 digits) and YYYY is the year.
 *
 * @param {string} dateString - The date string to validate.
 * @returns {RegExpMatchArray|null} The match result if the input is valid, or null if it does not match the pattern.
 *
 * @example
 * isWeekParam("2W2026"); // returns match array
 * isWeekParam("12W2024"); // returns match array
 * isWeekParam("20240101"); // returns null
 */
export const extractDateFromParam = (nwyyyy) => {
  const match = nwyyyy.match(/^(\d{1,2})W(\d{4})$/);
  if (!match) {
    L.warn('extractDateFromParam()', 'Invalid format. Expected NWYYYY (e.g. 2W2026)');
    return null;
  }

  return {
    week: fromAnyToInteger(match[1]),
    year: fromAnyToInteger(match[2]),
  };
};

/**
 * Returns the `Date` for the Monday of a given ISO week in a given year (anchored at UTC noon).
 *
 * Follows ISO-8601 week date rules:
 * - ISO week 1 is the week containing January 4th.
 * - Weeks start on Monday.
 * - The result is stabilized at UTC noon to avoid DST/local timezone edge cases.
 *
 * If `week` or `year` cannot be parsed as integers (via `fromAnyToInteger`), this function logs a warning and returns `undefined`.
 *
 * @param {number|string} week - The ISO week number (typically 1–53). Accepts numeric strings.
 * @param {number|string} year - The ISO year. Accepts numeric strings.
 * @returns {Date|null} A `Date` representing the Monday (UTC noon) of the requested ISO week, or `null` if parameters are invalid.
 *
 * @example
 * // Monday of ISO week 1 in 2025 (UTC noon)
 * const d = determineISOMondayOfWeek(1, 2025); // Date in UTC
 *
 * @example
 * // Accepts numeric strings as well
 * const d = determineISOMondayOfWeek("12", "2024");
 */
export const determineISOMondayOfWeek = (week, year) => {
  if (!fromAnyToInteger(week) || !fromAnyToInteger(year)) {
    L.warn('determineISOMondayOfWeek()', 'Invalid parameters. `week` or `year` is not a number.');
    return null;
  }
  // Jan 4th is always in ISO week 1
  const jan4 = new Date(Date.UTC(year, 0, 4, 12)); // 👈 UTC NOON

  const jan4Day = jan4.getUTCDay() || 7; // Sunday = 7
  const mondayOfWeek1 = new Date(jan4.getTime() - (jan4Day - 1) * ONE_DAY_IN_MILLIS);

  // Anchor result at UTC noon as well
  return new Date(mondayOfWeek1.getTime() + (week - 1) * 7 * ONE_DAY_IN_MILLIS);
};

/**
 * Determines the UTC start and end `Date`s for a given date in a given timezone.
 * @param {string} nwyyyy - The date string in the format "W12-2025".
 * @param {string} locale - The locale to use for the date formatting.
 * @param {string} timeZone - The timezone to use for the date formatting.
 * @returns {Object} An object with the UTC start and end.
 */
export const determineWeekRangeUTCForTimezone = (input, locale, timeZone) => {
  // If input is a Date, just get its week's range
  if (input instanceof Date) {
    const { startUTCDate: startUTC } = determineDayRangeUTCForTimezone(input, locale, timeZone);
    const endUTC = new Date(startUTC.getTime() + 7 * ONE_DAY_IN_MILLIS - 1);
    return {
      startUTCDate: startUTC,
      startUTCString: startUTC.toISOString(),
      endUTCDate: endUTC,
      endUTCString: endUTC.toISOString(),
    };
  }
  // If it's a "W122026" string (or similar), parse it as before
  const { week, year } = extractDateFromParam(input);

  // Monday of the ISO week (UTC reference)
  const monday = determineISOMondayOfWeek(week, year);

  // Start of Monday (timezone → UTC)
  const dayRange = determineDayRangeUTCForTimezone(monday, locale, timeZone);

  if (!dayRange) {
    L.warn('determineWeekRangeUTCForTimezone()', 'Invalid date:', monday, 'locale:', locale, 'timeZone:', timeZone);
    return null;
  }

  // End of Sunday = next Monday midnight
  const endUTC = new Date(dayRange.startUTCDate.getTime() + 7 * ONE_DAY_IN_MILLIS - 1);

  return {
    startUTCDate: dayRange.startUTCDate,
    startUTCString: dayRange.startUTCString,
    endUTCDate: endUTC,
    endUTCString: endUTC.toISOString(),
  };
};

/**
 * Determines the UTC start and end `Date`s for a given date.
 * @param {Date} date - The date to determine the start and end for.
 * @returns {Object} An object with the UTC start and end.
 */
export const getWeekRangeUTC = (date) => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay() || 7; // Make Sunday = 7
  const start = new Date(d);
  start.setUTCDate(d.getUTCDate() - day + 1);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);

  const startUTCDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const endUTCDate = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));

  return {
    startUTCDate,
    startUTCString: startUTCDate.toISOString(),
    endUTCDate,
    endUTCString: endUTCDate.toISOString(),
  };
};
